/**
 * How Marvin's action cards read on the native screens (boards W-05, W-06, W-07): the markup card's title,
 * rows and preview region, an approval's details as typed rows and chips (never JSON), a saved RFI draft's
 * fields, and the Deep research offer's words. Pure, so the engine sends native every word ready
 * (`engineTurn.ts`) and the web reads the shared ones (outcome lines, offer copy) from here too.
 */
import {
    MARKUP_ROW_NOUNS,
    asMarkupCard,
    previewRegion,
    type MarkupBox,
    type MarkupAppearance,
    type MarkupGeometry,
    type MarkupRowKind,
} from "../../components/marvin/markup/markupCards";
import { asDraftCard } from "../../components/marvin/rfi/rfiCards";
import type { MarvinDigDeeperWidget } from "../../shared/marvinWidgets";
import type { MarvinAction, MarvinActionFinalStatus } from "../messagePartTypes";

/* ------------------------------------------------------------------ outcomes */

/** What a decided action's card says when the server sent no `resultSummary` of its own. */
export const ACTION_OUTCOME_FALLBACK: Record<MarvinActionFinalStatus, string> = {
    executed: "Done.",
    failed: "The action failed.",
    rejected: "You declined this action.",
    expired: "No response — the action was not performed.",
};

/** A decision the server did not take (network, 5xx): the card keeps its buttons. */
export const ACTION_DECISION_ERROR = "Couldn't submit your decision. Please try again.";

/** A pending card with no run to decide against (rebuilt from history without one). */
export const ACTION_INERT_TEXT = "This action can no longer be decided from this view.";

const FINAL_STATUSES: MarvinActionFinalStatus[] = ["executed", "failed", "rejected", "expired"];

/** Each final status's line: the action's own `resultSummary` for the status it has, else the fallback. */
export function actionOutcomes(action: MarvinAction): Record<MarvinActionFinalStatus, string> {
    const outcomes = { ...ACTION_OUTCOME_FALLBACK };
    for (const status of FINAL_STATUSES) {
        if (action.status === status && action.resultSummary) {
            outcomes[status] = action.resultSummary;
        }
    }
    return outcomes;
}

/* ------------------------------------------------------------------ approval details */

/** One row of an approval's details: words, or chips (sheets, files), never a JSON dump. */
export interface ActionDetailRow {
    key: string;
    label: string;
    /** The value in words (the card clamps a long one). */
    text?: string;
    /** Sheet codes or file names, drawn as chips. */
    chips?: string[];
}

export interface ActionDetails {
    /** The thing the action makes, in bold under the title ("Level 2 D1 Composite Deck — …"). */
    headline?: string;
    /** The summary, without the title and headline it would repeat. */
    summary: string;
    rows: ActionDetailRow[];
}

/** Keys that are backend plumbing (UUIDs and id handles): meaningless on a card. */
const isPlumbingKey = (key: string): boolean => /(^|_)ids?$|Ids?$|_uuid$|^id$/.test(key);
/** Keys whose value names the action's subject: shown as the headline, not a row. */
const HEADLINE_KEYS = ["subject", "title", "name"];
/** Keys whose value lists sheets: shown as sheet chips. */
const SHEET_KEYS = new Set([
    "reference_codes",
    "references",
    "sheets",
    "sheet_codes",
    "drawing_references",
    "group_code",
]);
const FILE_KEYS = new Set(["attachments", "files"]);
const LABELS: Record<string, string> = {
    reference_codes: "Sheets",
    references: "Sheets",
    sheets: "Sheets",
    sheet_codes: "Sheets",
    drawing_references: "Sheets",
    group_code: "Sheet",
    h_id: "Detail",
    rfi_number: "RFI",
};

const isRecord = (value: unknown): value is Record<string, unknown> =>
    typeof value === "object" && value !== null && !Array.isArray(value);

/** snake_case / camelCase key → "Sentence case" label. */
function labelOf(key: string): string {
    if (LABELS[key]) return LABELS[key];
    const words = key
        .replace(/[_-]+/g, " ")
        .replace(/([a-z])([A-Z])/g, "$1 $2")
        .trim()
        .toLowerCase();
    return words.charAt(0).toUpperCase() + words.slice(1);
}

/** A list value as chip labels: strings as they are, objects by their name or label; nothing else. */
function chipLabels(value: unknown): string[] {
    const items = Array.isArray(value)
        ? value
        : typeof value === "string"
          ? value.split(/\s*[,;]\s*/)
          : [];
    return items
        .map((item) =>
            typeof item === "string"
                ? item
                : isRecord(item)
                  ? [item.name, item.label, item.code].find((text) => typeof text === "string")
                  : undefined,
        )
        .filter((text): text is string => typeof text === "string")
        .map((text) => text.split("\n")[0].replace(/\s+/g, " ").trim())
        .filter((text) => text.length > 0);
}

/** A scalar value in words; a list of words joined; anything else (an object) is not shown. */
function wordsOf(value: unknown): string | null {
    if (typeof value === "string") return value.trim() || null;
    if (typeof value === "number" || typeof value === "boolean") return String(value);
    if (
        Array.isArray(value) &&
        value.length > 0 &&
        value.every((item) => typeof item === "string")
    ) {
        return value.join(", ");
    }
    return null;
}

/** The summary less a leading `<title> "<headline>" · refs …. ` it would repeat under the headline. */
function shortSummary(action: MarvinAction, headline: string | undefined): string {
    const summary = action.summary.trim();
    if (!headline || !summary.startsWith(action.title)) return summary;
    const quoted = summary.indexOf(`"${headline}"`);
    if (quoted < 0) return summary;
    const end = summary.indexOf(". ", quoted + headline.length + 2);
    return end < 0 ? summary : summary.slice(end + 2).trim();
}

/**
 * An approval's details, from its payload: the headline, then one row per field in the payload's order,
 * plumbing and empty fields dropped. Sheets read as sheet chips; attachments as file chips, folded into the
 * sheet row when they name the same sheets. A value that is an object is never shown as JSON.
 */
export function actionDetails(action: MarvinAction): ActionDetails {
    const payload = isRecord(action.payload) ? action.payload : {};
    const headlineKey = HEADLINE_KEYS.find(
        (key) => typeof payload[key] === "string" && (payload[key] as string).trim(),
    );
    const headline = headlineKey ? (payload[headlineKey] as string).trim() : undefined;
    const rows: ActionDetailRow[] = [];
    let sheetRow: ActionDetailRow | undefined;
    let fileRow: ActionDetailRow | undefined;
    for (const [key, value] of Object.entries(payload)) {
        if (key === headlineKey || isPlumbingKey(key)) continue;
        if (SHEET_KEYS.has(key)) {
            const chips = chipLabels(value);
            if (chips.length === 0) continue;
            sheetRow = { key, label: labelOf(key), chips };
            rows.push(sheetRow);
        } else if (FILE_KEYS.has(key)) {
            const chips = chipLabels(value);
            if (chips.length === 0) continue;
            fileRow = { key, label: labelOf(key), chips };
            rows.push(fileRow);
        } else {
            const text = wordsOf(value);
            if (!text) continue;
            rows.push({ key, label: labelOf(key), text });
        }
    }
    // Attachments that are the sheets the row already names ("S2.201 · SECOND FLOOR …") read once.
    const sheetChips = sheetRow?.chips;
    const folded =
        sheetChips && fileRow?.chips
            ? fileRow.chips.every((name) => sheetChips.some((code) => name.startsWith(code)))
            : false;
    return {
        ...(headline ? { headline } : {}),
        summary: shortSummary(action, headline),
        rows: folded ? rows.filter((row) => row !== fileRow) : rows,
    };
}

/* ------------------------------------------------------------------ the saved RFI draft */

export interface DraftCardField {
    key: string;
    label: string;
    value: string;
    /** The subject reads in bold. */
    strong?: boolean;
    /** Where the value came from, as a pill ("Suggested"). */
    pill?: string;
    /** Sheet codes, drawn as chips in place of the value. */
    chips?: string[];
}

export interface DraftCardView {
    /** "Draft RFI created", "Draft RFI updated". */
    title: string;
    subject: string;
    /** The draft's page, for Open draft. */
    href: string;
    fields: DraftCardField[];
    /** The attached snapshot and files, in the server's words. */
    attachments: string[];
    /** "Saved as an unsent draft: [subject](href)." (markdown, its link the draft's page). */
    reply?: string;
    openText: string;
}

const DRAFT_SHEET_FIELDS = new Set(["references"]);

/** A saved draft's card (`rfi_draft_create` / `rfi_draft_update` executed), or null when its result is not one. */
export function draftCardView(action: MarvinAction): DraftCardView | null {
    if (action.status !== "executed") return null;
    const card = asDraftCard(action.result);
    if (!card) return null;
    const reply = isRecord(action.result) ? action.result.reply : undefined;
    return {
        title: action.actionType === "rfi_draft_update" ? "Draft RFI updated" : "Draft RFI created",
        subject: card.subject,
        href: card.href,
        fields: card.fields
            .filter((field) => field.value.trim().length > 0)
            .map((field) => {
                const chips = DRAFT_SHEET_FIELDS.has(field.key) ? chipLabels(field.value) : [];
                return {
                    key: field.key,
                    label: field.label,
                    value: field.value,
                    ...(field.key === "subject" ? { strong: true } : {}),
                    ...(field.source?.label ? { pill: field.source.label } : {}),
                    ...(chips.length > 0 ? { chips } : {}),
                };
            }),
        attachments: (card.attachments ?? []).map((attachment) =>
            attachment.section_id
                ? attachment.note || "Snapshot attached"
                : [attachment.name, attachment.note].filter(Boolean).join(" · "),
        ),
        ...(typeof reply === "string" && reply.trim() ? { reply: reply.trim() } : {}),
        openText: "Open draft",
    };
}

/* ------------------------------------------------------------------ the markup card */

export interface MarkupCardRowView extends MarkupAppearance {
    shapeId: string;
    kind: MarkupRowKind;
    /** "Box", "Note · Exit 2". */
    text: string;
    geometry?: MarkupGeometry;
}

export interface MarkupCardView {
    fileId: string;
    page: number;
    sheetId: string;
    documentId: string | null;
    revisionId: string | null;
    sheetLabel: string;
    count: number;
    /** "13 markups on MA011". */
    title: string;
    rows: MarkupCardRowView[];
    /** The sheet region the preview crops (`previewRegion`); null when no row says where it sits. */
    region: MarkupBox | null;
    /** A list this long starts folded to its first rows, under "Show all 13". */
    foldedRows: number;
    showAllText: string;
    hideText: string;
}

/** A list longer than this starts folded (the web card's `LONG_LIST`). */
const LONG_LIST = 5;
/** How many rows a folded list still shows. */
const FOLDED_ROWS = 2;

/** `markup_save`'s card, or null when the action has none (a failure, an older payload). */
export function markupCardView(action: MarvinAction): MarkupCardView | null {
    const card = asMarkupCard(action.result);
    if (!card) return null;
    return {
        fileId: card.fileId,
        page: card.page,
        sheetId: card.sheetId,
        documentId: card.documentId,
        revisionId: card.revisionId,
        sheetLabel: card.sheetLabel,
        count: card.count,
        title: `${card.count} ${card.count === 1 ? "markup" : "markups"} on ${card.sheetLabel}`,
        rows: card.rows.map((row) => ({
            shapeId: row.shapeId,
            kind: row.kind,
            text: `${MARKUP_ROW_NOUNS[row.kind]}${row.label ? ` · ${row.label}` : ""}`,
            ...(row.geometry ? { geometry: row.geometry } : {}),
            ...(row.color ? { color: row.color } : {}),
            ...(row.fillColor && row.fillOpacity !== undefined
                ? { fillColor: row.fillColor, fillOpacity: row.fillOpacity }
                : {}),
        })),
        region: previewRegion(card.rows),
        foldedRows: card.rows.length > LONG_LIST ? FOLDED_ROWS : card.rows.length,
        showAllText: `Show all ${card.count}`,
        hideText: "Hide the list",
    };
}

/* ------------------------------------------------------------------ the Deep research offer */

export interface DeepResearchOfferWords {
    /** "Deep research · suggested", when Marvin suggests it. */
    eyebrow?: string;
    text: string;
    /** What the card says while a Deep research run is going in the thread. */
    runningText: string;
    /** "Not now", when Marvin suggests it. */
    dismissText?: string;
    actionText: string;
}

export const DEEP_RESEARCH_RUNNING_TEXT = "Deep research is already running.";

/** The offer's words: counted when Marvin suggests reading a known number of items. */
export function deepResearchOfferText(widget: MarvinDigDeeperWidget, running: boolean): string {
    if (running) return DEEP_RESEARCH_RUNNING_TEXT;
    return widget.suggested === true && typeof widget.item_count === "number"
        ? `Read all ${widget.item_count} ${widget.item_noun ?? "items"}? This might take a while.`
        : "I can research this in depth, but it might take a while.";
}

export function deepResearchOfferWords(widget: MarvinDigDeeperWidget): DeepResearchOfferWords {
    const suggested = widget.suggested === true;
    return {
        ...(suggested ? { eyebrow: "Deep research · suggested" } : {}),
        text: deepResearchOfferText(widget, false),
        runningText: DEEP_RESEARCH_RUNNING_TEXT,
        ...(suggested ? { dismissText: "Not now" } : {}),
        actionText: "Deep research",
    };
}
